import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  Bell,
  Building2,
  CheckCircle2,
  Compass,
  Layers3,
  Sparkles,
  X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Restructured guided tour — exactly 4 steps:
 *   1. Welcome  — centered "You're verified" modal (Start tour / Skip).
 *   2. Listings — spotlight the Add Listing action.
 *   3. Requirements — spotlight the Add Requirement action.
 *   4. AI Matching & Alerts — spotlight the Matches nav + notification bell
 *      (sequentially highlighted within the same conceptual step).
 *
 * Completion posts /api/auth/onboarding-complete so the tour only fires once.
 */

const SPOTLIGHT_STEPS = [
  {
    testId: "add-listing-button",
    fallbackTestIds: ["nav-inventory"],
    routeHint: "/dashboard/inventory",
    icon: Building2,
    title: "Add your first listing",
    body:
      "Paste your raw property write-up and MatchBroker's AI structures it into " +
      "a clean listing — Exclusive ATS included. This is how you seed inventory.",
  },
  {
    testId: "add-requirement-button",
    fallbackTestIds: ["nav-requirements"],
    routeHint: "/dashboard/requirements",
    icon: Compass,
    title: "Capture a buyer requirement",
    body:
      "Paste your client's brief — budget, area, must-haves — and the network " +
      "sources the moment a matching listing appears.",
  },
  {
    // Composite step — cycles between the Matches nav item and the bell
    // via a sub-target rotation handled below.
    testId: "nav-matches",
    fallbackTestIds: ["notification-bell-trigger"],
    subTargets: ["nav-matches", "notification-bell-trigger"],
    routeHint: "/dashboard/matches",
    icon: Layers3,
    title: "AI Matching & Alerts",
    body:
      "Every AI-scored match lands under Matches with a co-broker score on the " +
      "other party. New match alerts also appear in the bell — so you're never " +
      "chasing WhatsApp threads.",
  },
];

function useElementRect(testIds, ping) {
  const [rect, setRect] = useState(null);
  const [foundTestId, setFoundTestId] = useState(null);
  useEffect(() => {
    if (!testIds || testIds.length === 0) return undefined;
    const compute = () => {
      for (const id of testIds) {
        const el = document.querySelector(`[data-testid="${id}"]`);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) {
            setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            setFoundTestId(id);
            return;
          }
        }
      }
      setRect(null);
      setFoundTestId(null);
    };
    compute();
    const t = setInterval(compute, 300);
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      clearInterval(t);
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [JSON.stringify(testIds), ping]);
  return { rect, foundTestId };
}

function WelcomeModal({ open, onStart, onSkip, brokerName }) {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 px-6 backdrop-blur-sm"
      data-testid="onboarding-welcome"
    >
      <div
        className="w-full max-w-md rounded-3xl border border-white/20 bg-white p-8 text-center shadow-[0_28px_80px_rgba(0,0,0,0.35)] animate-mb-fade-in sm:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-green/10">
          <CheckCircle2
            className="h-8 w-8 text-brand-green"
            data-testid="onboarding-welcome-icon"
          />
        </div>
        <div className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-brand-green/25 bg-brand-green/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-green">
          <Sparkles className="h-3 w-3" />
          PRC-verified broker
        </div>
        <h2
          className="mt-5 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
          data-testid="onboarding-welcome-title"
        >
          You&apos;re verified.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-gray-500">
          Welcome to MatchBroker, {brokerName || "friend"}. Your PRC license
          was reviewed and approved — you&apos;re now inside Manila&apos;s
          exclusive network for verified brokers.
        </p>
        <p className="mt-3 text-sm text-gray-500">
          A 60-second tour will show you the three moves that make MatchBroker work.
        </p>
        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={onSkip}
            className="mb-focus-ring h-11 rounded-xl border-gray-200 bg-white px-6 text-sm font-semibold text-gray-600 hover:bg-gray-50"
            data-testid="onboarding-welcome-skip"
          >
            Skip tour
          </Button>
          <Button
            type="button"
            onClick={onStart}
            className="mb-focus-ring h-11 rounded-xl bg-brand-navy px-6 text-sm font-semibold text-white hover:bg-brand-navy-hover"
            data-testid="onboarding-welcome-start"
          >
            Start tour
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function OnboardingTour({ open, onClose }) {
  const [phase, setPhase] = useState("welcome"); // "welcome" | "spotlight"
  const [stepIdx, setStepIdx] = useState(0);
  const [subIdx, setSubIdx] = useState(0);
  const [posPing, setPosPing] = useState(0);
  const { user, refreshMe } = useAuth();

  const step = SPOTLIGHT_STEPS[stepIdx];
  // Composite step 3 cycles through subTargets; simpler steps use a single id.
  const activeTestIds = useMemo(() => {
    if (!step) return [];
    if (step.subTargets && step.subTargets.length > 0) {
      const id = step.subTargets[subIdx] || step.subTargets[0];
      return [id];
    }
    return [step.testId, ...(step.fallbackTestIds || [])];
  }, [step, subIdx]);
  const { rect, foundTestId } = useElementRect(activeTestIds, posPing);

  useEffect(() => {
    if (open) {
      setPhase("welcome");
      setStepIdx(0);
      setSubIdx(0);
    }
  }, [open]);

  // If the current step's target can't be found on this route, gently
  // navigate to the route hint (once per step change).
  useEffect(() => {
    if (phase !== "spotlight" || !step) return;
    if (foundTestId) return;
    if (!step.routeHint) return;
    if (window.location.pathname.startsWith(step.routeHint)) return;
    const t = setTimeout(() => {
      window.history.pushState({}, "", step.routeHint);
      window.dispatchEvent(new PopStateEvent("popstate"));
      setPosPing((p) => p + 1);
    }, 800);
    return () => clearTimeout(t);
  }, [phase, stepIdx, foundTestId, step]);

  const tooltipStyle = useMemo(() => {
    if (!rect) {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 340 };
    }
    const isBottom = rect.top < 220;
    const top = isBottom ? rect.top + rect.height + 16 : rect.top - 16;
    const cardWidth = Math.min(360, window.innerWidth - 32);
    const centerX = rect.left + rect.width / 2;
    const rawLeft = centerX - cardWidth / 2;
    const clampedLeft = Math.max(16, Math.min(rawLeft, window.innerWidth - cardWidth - 16));
    return {
      top,
      left: clampedLeft,
      transform: isBottom ? "none" : "translateY(-100%)",
      width: cardWidth,
    };
  }, [rect]);

  const persistComplete = async () => {
    try {
      await api.post("/auth/onboarding-complete", { completed: true });
      await refreshMe();
    } catch (e) {
      toast.error(formatApiError(e, "Could not save onboarding"));
    }
  };

  const finish = async () => {
    await persistComplete();
    toast.success("You're all set — happy brokering.");
    onClose();
  };

  const skip = async () => {
    await persistComplete();
    onClose();
  };

  const next = () => {
    if (step?.subTargets && subIdx < step.subTargets.length - 1) {
      setSubIdx((i) => i + 1);
      return;
    }
    if (stepIdx < SPOTLIGHT_STEPS.length - 1) {
      setStepIdx((i) => i + 1);
      setSubIdx(0);
      return;
    }
    finish();
  };

  const back = () => {
    if (subIdx > 0) {
      setSubIdx((i) => i - 1);
      return;
    }
    if (stepIdx > 0) {
      const prevIdx = stepIdx - 1;
      setStepIdx(prevIdx);
      const prev = SPOTLIGHT_STEPS[prevIdx];
      setSubIdx(prev?.subTargets ? prev.subTargets.length - 1 : 0);
    }
  };

  if (!open) return null;

  if (phase === "welcome") {
    return (
      <WelcomeModal
        open
        brokerName={user?.full_name?.split(" ")[0]}
        onStart={() => setPhase("spotlight")}
        onSkip={skip}
      />
    );
  }

  const HOLE_PAD = 8;
  const holeLeft = rect ? rect.left - HOLE_PAD : window.innerWidth / 2;
  const holeTop = rect ? rect.top - HOLE_PAD : window.innerHeight / 2;
  const holeW = rect ? rect.width + HOLE_PAD * 2 : 0;
  const holeH = rect ? rect.height + HOLE_PAD * 2 : 0;

  const isFinalStep = stepIdx === SPOTLIGHT_STEPS.length - 1;
  const isFinalSubOfComposite =
    !step?.subTargets || subIdx === step.subTargets.length - 1;
  const isLast = isFinalStep && isFinalSubOfComposite;

  const Icon = step?.icon || Sparkles;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] pointer-events-none"
      data-testid="onboarding-tour"
    >
      <svg
        className="pointer-events-auto absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <mask id="mb-tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={holeLeft}
                y={holeTop}
                width={holeW}
                height={holeH}
                rx="14"
                ry="14"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(11,22,42,0.55)"
          mask="url(#mb-tour-mask)"
        />
      </svg>

      <div
        className="pointer-events-auto absolute rounded-2xl border border-white/20 bg-white p-5 shadow-[0_24px_60px_rgba(0,0,0,0.35)] animate-mb-fade-in"
        style={tooltipStyle}
        data-testid={`onboarding-step-${stepIdx + 1}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-navy">
            <Icon className="h-3 w-3" />
            Step {stepIdx + 2} of 4
          </div>
          <button
            type="button"
            onClick={skip}
            className="text-gray-400 transition hover:text-gray-700"
            aria-label="Skip tour"
            data-testid="onboarding-skip"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <div className="text-lg font-semibold text-gray-900">{step.title}</div>
          {step?.subTargets && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0 text-[10px] font-semibold text-gray-500"
              data-testid="onboarding-substep-indicator"
            >
              {foundTestId === "notification-bell-trigger" ? (
                <>
                  <Bell className="h-2.5 w-2.5" /> Alerts
                </>
              ) : (
                <>
                  <Layers3 className="h-2.5 w-2.5" /> Matches
                </>
              )}
            </span>
          )}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">{step.body}</p>
        <div className="mt-4 flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5" aria-hidden>
            {/* 4 pips: welcome (done) + 3 spotlight steps */}
            <span className="h-1.5 w-1.5 rounded-full bg-brand-navy/30" />
            {SPOTLIGHT_STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition ${
                  i === stepIdx ? "w-6 bg-brand-navy" : "w-1.5 bg-gray-200"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {(stepIdx > 0 || subIdx > 0) && (
              <button
                type="button"
                onClick={back}
                className="rounded-lg px-2.5 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100"
                data-testid="onboarding-back"
              >
                Back
              </button>
            )}
            {isLast ? (
              <button
                type="button"
                onClick={finish}
                className="mb-focus-ring inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-hover"
                data-testid="onboarding-finish"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Got it
              </button>
            ) : (
              <button
                type="button"
                onClick={next}
                className="mb-focus-ring inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-hover"
                data-testid="onboarding-next"
              >
                Next
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
